<template>
  <div class="sp-theme-color-picker">
    <div
      v-for="colorName in colorNames"
      :key="colorName"
      :ref="colorName"
      :title="colorName"
      :class="getColorBoxClass(colorName)"
      class="sp-theme-color-picker__color-box"
      @click="setColor(colorName)"
    >
      <div class="sp-theme-color-picker__color-box-color" :style="getColorBoxStyle(colorName)"></div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { isSystemColor, systemColors, toSystemColorVariable } from "#ce/composables/color";

const NONE_COLOR = "none";

const emit = defineEmits(["update-model-value"]);

const props = defineProps({
  value: {
    type: String,
    default: undefined,
  },
});

const model = computed({
  get: () => props.value,
  set: (value) => emit("update-model-value", value),
});

const colorNames = computed(() => [NONE_COLOR, ...systemColors]);
const hasSelectedColor = computed(() => isSystemColor(model.value));

function getColorBoxClass(colorName) {
  return { "--selected": isSelectedColor(colorName), "--none": isNoneColor(colorName) };
}

function getColorBoxStyle(colorName) {
  return { backgroundColor: toSystemColorVariable(colorName) };
}

function setColor(colorName) {
  model.value = isNoneColor(colorName) ? undefined : colorName;
}

/**
 * Returns true if the color is selected.
 * If the color is none, returns true if no color is selected.
 * If the color is not none, returns true if the color is the same as the model value.
 *
 * @param {string} colorName
 * @returns {boolean}
 */
function isSelectedColor(colorName) {
  if (isNoneColor(colorName)) {
    return !hasSelectedColor.value;
  }

  return colorName === model.value;
}

function isNoneColor(colorName) {
  return colorName === NONE_COLOR;
}
</script>

<style scoped lang="scss">
.sp-theme-color-picker {
  --gap: 0.5rem;

  display: flex;
  flex-direction: row;
  gap: var(--gap);
  padding: 1rem;
  background: var(--sp-sys-color-primary-container);
  border-radius: 0.5rem;
}

.sp-theme-color-picker__color-box {
  display: flex;
  flex-direction: column;
  gap: var(--gap);
  cursor: pointer;

  &.--selected {
    .sp-theme-color-picker__color-box-color {
      transform: scale(1.2);
      outline-color: var(--sp-sys-color-primary);
    }
  }

  &.--none {
    .sp-theme-color-picker__color-box-color {
      background-image:
        linear-gradient(45deg, var(--sp-sys-color-outline) 25%, transparent 25%),
        linear-gradient(-45deg, var(--sp-sys-color-outline) 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, var(--sp-sys-color-outline) 75%),
        linear-gradient(-45deg, transparent 75%, var(--sp-sys-color-outline) 75%);
      background-size: 4px 4px;
      background-position:
        0 0,
        0 2px,
        2px -2px,
        -2px 0px;
      background-color: transparent !important;
    }
  }
}

.sp-theme-color-picker__color-box-color {
  --size: 1.5rem;
  width: var(--size);
  height: var(--size);
  border-radius: 50%;
  outline: 1px solid hsla(0, 0%, 100%, 0.5);
  transition:
    transform 0.2s ease,
    outline-color 0.2s ease;
}
</style>
